<template>

  <div class="class-gate">

    <template v-if="ui.state=='joining' || ui.state=='stuck'">
      <div class="subtitle">{{ ui.message }}</div>
      <div class="join">
        <a :href="data.class_link">Click HERE to Join</a>
      </div>
      <div class="note">If your class doesn't open by itself, tap the link above.</div>
    </template>

    <template v-else-if="ui.state=='bad_link'">
      <div class="subtitle">{{ ui.message }}</div>
      <div class="note">Waiting for your instructor to post a working meeting link.</div>
      <div class="stored-link">{{ data.attendance ? data.attendance.video_link : '' }}</div>
    </template>

    <template v-else-if="ui.state=='waiting'">
      <div class="subtitle">{{ ui.message }}</div>
      <div class="note">Waiting for your instructor to start the class. This page opens it automatically.</div>
    </template>

    <template v-else-if="ui.state=='cancelled'">
      <div class="subtitle">{{ ui.message }}</div>
      <div class="note">There is no class to join today.</div>
    </template>

    <template v-else>
      <div class="subtitle">{{ ui.message }}</div>
    </template>

  </div>

  <div class="extra-features">
    <div class="title">Material</div>
    <ClassMaterial/>
  </div>

</template>


<script setup>
  import {provide,reactive,onBeforeMount,onUnmounted} from 'vue'
  import {useRoute,useRouter} from 'vue-router'
  import {useStore} from 'vuex'
  import axios from 'axios'

  import ClassMaterial from '@/components/ClassMaterial.vue'


  let route=useRoute()
  let router=useRouter()
  let store=useStore()
  let user=store.state.user
  let group_id=''

  // The instructor picks the meeting link (Teams / Zoom / Meet) when confirming the class,
  // so it can show up minutes after the student opens this page. Keep re-reading until it does.
  const poll_ms=5000
  let poll=null
  let redirected=false

  const cancelling_statuses=[
    'no-show',
    'cancelled-late',
    'cancelled-on-time',
    'rescheduled',
    'cancelled-on-time-by-student',
    'cancelled-late-by-student',
    'cancelled-by-student',
  ]

  let data=reactive({
    class_link:"",
    attendance:null,
  })

  let ui=reactive({
    state:'loading',
    message:'Loading your class...',
  })

  let class_material_data=reactive({
    show:false,
    hide:true,
    attendance:null,
    user:null,
  })

  provide('class_material_data',class_material_data)


  async function confirmation(){
    let response=await axios.post('/students/confirmation/',{user_id:user.id,group_id:group_id,status:'confirmed'})
    console.log(response.data)
  }

  onBeforeMount(()=>{

    if (user==null){
      router.push('/login')
      return
    }

    group_id=route.params.link.split('-')[0]

    if (isNaN(parseInt(group_id))){
      console.log('Group ID not a number')
      ui.state='error'
      ui.message='This class link is not valid.'
      return
    }

    start()

  })

  onUnmounted(()=>{
    stop_polling()
  })


  async function start(){

    try{
      await get_attendance(group_id)
    }catch(e){
      console.log(e)
    }

    // Register attendance BEFORE leaving for the video call: navigating away can cancel an
    // in-flight request. One confirmation per visit to the link, on purpose.
    if (user.role=='Student'){
      ui.message='Registering your attendance...'
      try{
        await confirmation()
        await get_attendance(group_id)
      }catch(e){
        console.log(e)
      }
    }

    evaluate()

    if (!redirected){
      poll=setInterval(refresh,poll_ms)
    }

  }


  async function refresh(){
    try{
      await get_attendance(group_id)
    }catch(e){
      console.log(e)
      return
    }
    evaluate()
  }


  async function get_attendance(group_id){
    let response = await axios.post('/meet/get_attendance/',{group_id:parseInt(group_id)})
    class_material_data.attendance=response.data
    data.attendance=response.data
    return response.data
  }


  // Zoom/Outlook hand out a calendar-invite URL next to the join URL, and instructors save the
  // wrong one. The browser downloads it as a file instead of opening the class, so a redirect
  // to it silently does nothing.
  const calendar_link=/(\.ics(\?|$)|\/ics\?|icstoken=)/i

  // The instructor's meeting link lives on attendance.video_link. Anything on our own domain
  // is not a video call: it's either the class link itself (which points back at this page),
  // the login page written on a cancellation, or the retired meet. subdomain from Jitsi.
  function classify(attendance){
    let video_link=(attendance && attendance.video_link || '').trim()
    if (!video_link){
      return {kind:'none',url:''}
    }
    if (video_link==attendance.link){
      return {kind:'none',url:''}
    }
    if (video_link.includes('interactspeakingcenter.com')){
      return {kind:'none',url:''}
    }
    if (calendar_link.test(video_link)){
      return {kind:'calendar',url:video_link}
    }
    return {kind:'join',url:video_link}
  }


  function last_instructor_confirmation(attendance){
    let confirmations=attendance && attendance.instructor_confirmation
    if (!confirmations || confirmations.length==0){
      return null
    }
    return confirmations[confirmations.length-1]
  }


  function instructor_name(attendance){
    let confirmation=last_instructor_confirmation(attendance)
    return confirmation && confirmation.instructor ? confirmation.instructor.ui : ''
  }


  function evaluate(){
    let attendance=data.attendance

    if (!attendance || !attendance.id){
      // couldn't read today's class (network/server) - keep polling, don't claim anything
      ui.state='error'
      ui.message='We could not load your class. Retrying...'
      return
    }

    let video_link=classify(attendance)

    if (video_link.kind=='join'){
      join(video_link.url)
      return
    }

    if (video_link.kind=='calendar'){
      // keep polling: the moment the instructor saves a real join link we send them in
      ui.state='bad_link'
      ui.message="Your instructor's link is a calendar invite, not a meeting link"
      return
    }

    let confirmation=last_instructor_confirmation(attendance)
    let status=confirmation ? confirmation.confirmation : ''

    if (cancelling_statuses.indexOf(status)>-1){
      stop_polling()
      ui.state='cancelled'
      ui.message=`Today's class was marked "${status}"`
      return
    }

    let instructor=instructor_name(attendance)
    ui.state='waiting'
    ui.message=instructor ? `You're checked in for your class with ${instructor}` : "You're checked in"
  }


  function join(video_link){
    if (redirected){
      return
    }
    redirected=true
    stop_polling()

    data.class_link=video_link
    ui.state='joining'
    ui.message='Opening your class...'

    // replace() so coming back from the video call doesn't land on this page and bounce
    // the student straight back into the meeting.
    window.location.replace(video_link)

    // If the browser downloaded the link or refused the hop we are still sitting here, so
    // stop claiming the class is opening and let them tap it themselves.
    setTimeout(()=>{
      if (ui.state=='joining'){
        ui.state='stuck'
        ui.message="Your class didn't open automatically"
      }
    },3000)
  }


  function stop_polling(){
    if (poll){
      clearInterval(poll)
      poll=null
    }
  }

</script>



<style scoped>
  .class-gate{
    width:100%;
    min-height:300px;
    display:flex;
    flex-direction:column;
    justify-content:center;
    align-items:center;
    gap:20px;
    padding:20px;
    box-sizing:border-box;
    background:var(--white);
    color:var(--black);
    text-align:center;
  }

  .class-gate .subtitle{
    width:100%;
    max-width:600px;
    white-space:normal;
  }

  .note{
    width:100%;
    max-width:600px;
    font-size:18px;
  }

  .stored-link{
    width:100%;
    max-width:600px;
    font-size:12px;
    font-family:monospace;
    color:var(--gray);
    overflow-wrap:anywhere;
  }

  .join a{
    display:inline-block;
    padding:15px 25px;
    border:var(--border);
    border-radius:10px;
    background:var(--blue);
    color:var(--white);
    font-size:35px;
    text-decoration:none;
  }

  .extra-features{
    background:var(--white);
    color:var(--black);
    width: 100%;
    height: max-content;
  }
</style>
